<template>
  <!-- 导航栏 -->
  <nav class="bg-white shadow-sm sticky top-0 z-50">
    <div class="max-w-[1440px] mx-auto px-6 h-16 flex items-center justify-between">
      <div class="flex items-center space-x-8">
        <!-- Logo -->
        <router-link to="/" class="flex items-center space-x-2">
          <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <IconifyIcon class="text-white text-2xl" icon="mdi:handshake" />
          </div>
          <span class="text-xl font-bold text-blue-900 tracking-tight">神通债权</span>
        </router-link>
        <!-- 导航菜单 -->
        <div class="hidden md:flex items-center space-x-6 text-sm font-medium">
          <router-link 
            to="/" 
            :class="['transition', $route.path === '/' ? 'text-blue-600' : 'hover:text-blue-600']"
          >
            首页
          </router-link>
          <router-link 
            to="/square" 
            :class="['hover:text-blue-600 transition', $route.path === '/square' ? 'text-blue-600' : '']"
          >
            信息广场
          </router-link>
          <a
            @click="handleNavigateToPublish"
            class="hover:text-blue-600 transition cursor-pointer"
            :class="$route.path === '/publish' ? 'text-blue-600' : ''"
          >
            发布债权/债务
          </a>
          <a
            @click="handleNavigateToLegalDocs"
            class="hover:text-blue-600 transition cursor-pointer"
            :class="$route.path === '/legal-docs' ? 'text-blue-600' : ''"
          >
            代写文书
          </a>
          <router-link 
            to="/faq" 
            :class="['hover:text-blue-600 transition', $route.path === '/faq' ? 'text-blue-600' : '']"
          >
            常见问题
          </router-link>
          <router-link 
            to="/about" 
            :class="['hover:text-blue-600 transition', $route.path === '/about' ? 'text-blue-600' : '']"
          >
            关于我们
          </router-link>
          <router-link 
            to="/contact" 
            :class="['hover:text-blue-600 transition', $route.path === '/contact' ? 'text-blue-600' : '']"
          >
            联系我们
          </router-link>
        </div>
      </div>
      
      <!-- 右侧操作区 -->
      <div class="flex items-center space-x-4">
        <!-- 未登录状态 -->
        <template v-if="!isLoggedIn">
          <router-link :to="{ path: '/login', query: { redirect: $route.fullPath } }" class="text-sm font-medium hover:text-blue-600">
            登录
          </router-link>
        </template>
        
        <!-- 已登录状态 - 用户头像下拉菜单 -->
        <template v-else>
          <div 
            class="relative"
            @mouseenter="showDropdown = true"
            @mouseleave="showDropdown = false"
          >
            <button class="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden border border-slate-100 transition hover:ring-2 hover:ring-blue-100 focus:outline-none cursor-pointer">
              <img 
                :src="userInfo.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix'" 
                alt="User Avatar" 
                class="w-full h-full object-cover"
              />
            </button>
            <!-- 下拉菜单 -->
            <div 
              v-show="showDropdown"
              class="absolute right-0 top-full pt-2 z-50"
            >
              <div class="w-48 bg-white border border-slate-100 rounded-xl shadow-xl py-2">
              <div class="px-4 py-2 border-b border-slate-50 mb-1">
                <p class="text-sm font-bold text-slate-900">{{ userInfo.nickname || '用户' }}</p>
                <!-- <p class="text-xs text-slate-400 truncate">{{ maskPhone(userInfo.phone) }}</p> -->
              </div>
              <router-link 
                to="/profile" 
                class="flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <IconifyIcon icon="mdi:account-outline" />
                <span>个人中心</span>
              </router-link>
              <router-link 
                to="/my-orders" 
                class="flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <IconifyIcon icon="mdi:receipt-text-outline" />
                <span>我的订单</span>
              </router-link>
              <router-link 
                to="/my-claims" 
                class="flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <IconifyIcon icon="mdi:clipboard-text-outline" />
                <span>我的债权</span>
              </router-link>
              <router-link 
                to="/my-debts" 
                class="flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <IconifyIcon icon="mdi:account-alert-outline" />
                <span>我的债务</span>
              </router-link>
              <router-link 
                to="/my-favorites" 
                class="flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <IconifyIcon icon="mdi:star-outline" />
                <span>我的收藏</span>
              </router-link>
              <router-link 
                to="/my-legal-docs" 
                class="flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <IconifyIcon icon="mdi:file-document-outline" />
                <span>我的代写</span>
              </router-link>
              <div class="border-t border-slate-50 my-1"></div>
              <button 
                @click="handleLogout"
                class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
              >
                <IconifyIcon icon="mdi:logout" />
                <span>退出登录</span>
              </button>
            </div>
            </div>
          </div>
        </template>
        
        <!-- 快速发布按钮 -->
        <button
          @click="handleNavigateToPublish"
          class="bg-blue-600 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-blue-700 transition flex items-center cursor-pointer"
        >
          <IconifyIcon class="mr-1" icon="mdi:plus" />
          快速发布
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
import { getLogOut } from '@/api/index'
export default {
  name: 'AppHeader',
  data() {
    return {
      showDropdown: false,
      isLoggedIn: false,
      // isLoggedIn: true,
      userInfo: {
        nickname: '',
        // phone: '',
        avatar: ''
      }
    }
  },
  created() {
    // 检查登录状态
    this.checkLoginStatus()
  },
  watch: {
    // 每次路由变化时重新检查登录状态
    $route() {
      this.checkLoginStatus()
    }
  },
  methods: {
    checkLoginStatus() {
      // 从localStorage获取用户信息
      const userInfo = localStorage.getItem('userInfo')
      const token = localStorage.getItem('token')
      if (token && userInfo) {
        this.isLoggedIn = true
        this.userInfo = JSON.parse(userInfo)
      } else {
        this.isLoggedIn = false
        this.userInfo = {
          nickname: '',
          // phone: '',
          avatar: ''
        }
      }
    },
    maskPhone(phone) {
      if (!phone) return ''
      return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
    },
    // 导航到发布页面
    handleNavigateToPublish() {
      this.$router.push('/publish')
    },
    // 导航到代写文书页面
    handleNavigateToLegalDocs() {
      this.$router.push('/legal-docs')
    },
    async handleLogout() {
      // 二次确认
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        // 调用退出登录接口
        const res = await getLogOut()
        if (res.status == 200) {
          // 清除登录信息
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          this.isLoggedIn = false
          this.userInfo = {
            nickname: '',
            phone: '',
            avatar: ''
          }
          this.showDropdown = false
          this.$message.success(res.msg || '退出成功')
          // 跳转到登录页（避免重复跳转报错）
          if (this.$route.path !== '/login') {
            this.$router.push('/login')
          }
        }
      }).catch(() => {
        // 用户取消操作
      })
    }
  }
}
</script>

<style scoped>
/* 下拉菜单项悬停效果 */
a:hover,
button:hover {
  background-color: #eff6ff;
  color: #2563eb;
}

button:last-child:hover {
  background-color: #fef2f2;
  color: #dc2626;
}
</style>
